// client/src/components/services_tester/common/ServiceStatus.jsx
import React from 'react';
import { Box, Typography, Chip, Stack } from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
  HelpOutline,
} from '@mui/icons-material';

// ============================================================================
// Хелперы
// ============================================================================

function getStatusMeta(status) {
  if (!status) {
    return {
      key: 'unknown',
      color: 'default',
      icon: <HelpOutline sx={{ fontSize: 12 }} />,
      label: 'не проверено',
    };
  }

  switch (status.status) {
    case 'healthy':
    case 'connected':
    case 'success':
      return {
        key: status.status,
        color: 'success',
        icon: <CheckCircle sx={{ fontSize: 12 }} />,
        label: status.status,
      };
    case 'error':
    case 'unhealthy':
      return {
        key: status.status,
        color: 'error',
        icon: <ErrorIcon sx={{ fontSize: 12 }} />,
        label: status.status,
      };
    case 'warning':
      return {
        key: 'warning',
        color: 'warning',
        icon: <Warning sx={{ fontSize: 12 }} />,
        label: status.status,
      };
    case 'unknown':
      return {
        key: 'unknown',
        color: 'default',
        icon: <Info sx={{ fontSize: 12 }} />,
        label: 'unknown',
      };
    default:
      return {
        key: status.status || 'unknown',
        color: 'default',
        icon: <Info sx={{ fontSize: 12 }} />,
        label: status.status || 'unknown',
      };
  }
}

// ============================================================================
// Компонент
// ============================================================================

/**
 * Компактный статус сервиса.
 *
 * @param {object} props
 * @param {object} props.status — `{ status, message, responseTime, ... }`
 * @param {boolean} [props.compact=false] — компактный режим (одна строка)
 * @param {boolean} [props.showMessage=true] — показывать ли message
 * @param {boolean} [props.showResponseTime=true] — показывать ли responseTime
 */
export default function ServiceStatus({
  status,
  compact = false,
  showMessage = true,
  showResponseTime = true,
}) {
  const meta = getStatusMeta(status);
  const message = status?.message;
  const responseTime = status?.responseTime;

  // Компактный режим — только Chip
  if (compact) {
    return (
      <Chip
        icon={meta.icon}
        label={meta.label}
        size="small"
        color={meta.color}
        variant={meta.key === 'unknown' ? 'outlined' : 'filled'}
        sx={{
          height: 18,
          fontSize: '0.6rem',
          '& .MuiChip-label': { px: 0.5 },
          '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
        }}
      />
    );
  }

  // Полный режим — Chip + message + responseTime
  return (
    <Stack spacing={0.25}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip
          icon={meta.icon}
          label={meta.label}
          size="small"
          color={meta.color}
          variant={meta.key === 'unknown' ? 'outlined' : 'filled'}
          sx={{
            height: 20,
            fontSize: '0.65rem',
            '& .MuiChip-label': { px: 0.5 },
            '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
          }}
        />
        {showResponseTime && responseTime && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
            {responseTime} ms
          </Typography>
        )}
      </Box>
      {showMessage && message && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: '0.6rem',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            wordBreak: 'break-word',
          }}
        >
          {message}
        </Typography>
      )}
    </Stack>
  );
}
